import { useState, useEffect, useRef } from 'react';
import { useMail } from '../../context/MailContext';
import './ComposeModal.css';

const TEMPLATES = {
  formal: {
    label: 'Formal Business',
    body: `Dear [Name],\n\nI am writing to you regarding [subject matter]. Please find the details outlined below.\n\n[Your message here]\n\nKind regards,\nJohn Doe\nEllertek Ltd`,
  },
  followup: {
    label: 'Follow-Up',
    subject: 'Follow-up: [Topic]',
    body: `Hi [Name],\n\nI wanted to follow up on my previous email regarding [topic]. Please let me know if you need any further information.\n\nBest regards,\nJohn Doe`,
  },
  meeting: {
    label: 'Meeting Request',
    subject: 'Meeting Request — [Topic]',
    body: `Hi [Name],\n\nI'd like to schedule a meeting to discuss [topic].\n\nProposed time: [Date] at [Time]\nLocation: [Details]\n\nPlease let me know if this works for you.\n\nBest,\nJohn Doe`,
  },
  announcement: {
    label: 'Internal Announcement',
    subject: 'Announcement: [Title]',
    body: `Team,\n\nI am pleased to share that [announcement details].\n\nThis will take effect from [date].\n\nRegards,\nJohn Doe\nEllertek Ltd`,
  },
  inquiry: {
    label: 'Business Inquiry',
    subject: 'Inquiry — [Subject]',
    body: `Dear [Name/Team],\n\nI am reaching out to inquire about [subject].\n\nPlease feel free to contact me at your earliest convenience.\n\nSincerely,\nJohn Doe\nEllertek Ltd`,
  },
};

const SIGNATURE = `\n\n--\nJohn Doe\nEllertek Ltd | john.doe@ellertek.co.tz`;
const DEFAULT_W = 640;
const DEFAULT_H = 580;
const MIN_W = 400;
const MIN_H = 360;

export default function ComposeModal() {
  const { composeOpen, setComposeOpen, composeData, sendEmail, saveDraft } = useMail();

  const [tab, setTab] = useState('compose');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledFor, setScheduledFor] = useState('');
  const [requestReadReceipt, setRequestReadReceipt] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [isMaximized, setIsMaximized] = useState(false);

  // size & position — stored in refs to avoid re-render during drag
  const sizeRef = useRef({ w: DEFAULT_W, h: DEFAULT_H });
  const posRef = useRef({ x: null, y: null });
  const panelRef = useRef(null);
  const bodyRef = useRef(null);
  const dragState = useRef(null);
  const resizeState = useRef(null);

  useEffect(() => {
    if (!composeOpen) return;
    if (composeData) {
      setTo(composeData.to || '');
      setSubject(composeData.subject || '');
      setBody(composeData.body
        ? `\n\n--- Original message ---\n${composeData.body.replace(/<[^>]+>/g, '')}`
        : SIGNATURE);
    } else {
      setTo(''); setCc(''); setBcc('');
      setSubject(''); setBody(SIGNATURE);
    }
    setTab('compose');
    setSendError('');
    setScheduleMode(false);
    setMinimized(false);
    setIsMaximized(false);
    sizeRef.current = { w: DEFAULT_W, h: DEFAULT_H };
    posRef.current = { x: null, y: null };
    applyPanelStyle();
  }, [composeOpen, composeData]);

  function applyPanelStyle() {
    const el = panelRef.current;
    if (!el) return;
    const { w, h } = sizeRef.current;
    const { x, y } = posRef.current;
    el.style.width = w + 'px';
    el.style.height = h + 'px';
    if (x !== null) {
      el.style.left = x + 'px';
      el.style.top = y + 'px';
      el.style.right = 'auto';
      el.style.bottom = 'auto';
    } else {
      el.style.right = '16px';
      el.style.bottom = '16px';
      el.style.left = 'auto';
      el.style.top = 'auto';
    }
  }

  if (!composeOpen) return null;

  const handleClose = () => {
    setComposeOpen(false);
  };

  const handleSend = async () => {
    if (!to.trim()) { setSendError('Please enter a recipient email address.'); return; }
    setSending(true); setSendError('');
    try {
      await sendEmail({
        to: to.trim(), cc, bcc,
        subject: subject || '(No subject)',
        body: body.replace(/\n/g, '<br>'),
        scheduledFor: scheduleMode && scheduledFor ? new Date(scheduledFor) : null,
        requestReadReceipt,
      });
      handleClose();
    } catch (err) {
      setSendError(err.message || 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleDraft = () => {
    saveDraft({ to, subject: subject || '(No subject)', body: body.replace(/\n/g, '<br>') });
    handleClose();
  };

  const applyTemplate = (key) => {
    const t = TEMPLATES[key];
    if (t.subject) setSubject(prev => prev || t.subject);
    setBody(t.body + SIGNATURE);
    setTab('compose');
    setTimeout(() => bodyRef.current?.focus(), 50);
  };

  const applyFormat = (fmt) => {
    const area = bodyRef.current;
    if (!area) return;
    const start = area.selectionStart;
    const end = area.selectionEnd;
    const sel = body.slice(start, end) || (fmt === 'bullet' ? 'Item' : 'text');
    let result = sel;
    if (fmt === 'bold') result = `**${sel}**`;
    if (fmt === 'italic') result = `_${sel}_`;
    if (fmt === 'underline') result = `__${sel}__`;
    if (fmt === 'bullet') result = sel.split('\n').map(l => `• ${l}`).join('\n');
    setBody(body.slice(0, start) + result + body.slice(end));
    setTimeout(() => { area.focus(); area.setSelectionRange(start, start + result.length); }, 0);
  };

  const toggleMaximize = () => {
    const el = panelRef.current;
    if (!el) return;
    if (isMaximized) {
      sizeRef.current = { w: DEFAULT_W, h: DEFAULT_H };
      posRef.current = { x: null, y: null };
      setIsMaximized(false);
    } else {
      sizeRef.current = { w: window.innerWidth - 32, h: window.innerHeight - 80 };
      posRef.current = { x: 16, y: 16 };
      setIsMaximized(true);
    }
    setTimeout(applyPanelStyle, 0);
  };

  // ── Drag header ────────────────────────────────────────────────────────────
  const onHeaderMouseDown = (e) => {
    if (isMaximized || e.target.tagName === 'BUTTON') return;
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragState.current = {
      startX: e.clientX, startY: e.clientY,
      startLeft: rect.left, startTop: rect.top,
    };
    e.preventDefault();
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragUp);
  };

  const onDragMove = (e) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    posRef.current = {
      x: Math.max(0, dragState.current.startLeft + dx),
      y: Math.max(0, dragState.current.startTop + dy),
    };
    applyPanelStyle();
  };

  const onDragUp = () => {
    dragState.current = null;
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', onDragUp);
  };

  // ── Resize handles ─────────────────────────────────────────────────────────
  const onResizeMouseDown = (e, edge) => {
    if (isMaximized) return;
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    resizeState.current = {
      edge, startX: e.clientX, startY: e.clientY,
      startW: rect.width, startH: rect.height,
      startLeft: rect.left, startTop: rect.top,
    };
    e.preventDefault(); e.stopPropagation();
    window.addEventListener('mousemove', onResizeMove);
    window.addEventListener('mouseup', onResizeUp);
  };

  const onResizeMove = (e) => {
    if (!resizeState.current) return;
    const r = resizeState.current;
    const dx = e.clientX - r.startX;
    const dy = e.clientY - r.startY;
    let w = r.startW, h = r.startH;
    let x = posRef.current.x !== null ? posRef.current.x : r.startLeft;
    let y = posRef.current.y !== null ? posRef.current.y : r.startTop;

    if (r.edge.includes('e')) w = Math.max(MIN_W, r.startW + dx);
    if (r.edge.includes('w')) { w = Math.max(MIN_W, r.startW - dx); x = r.startLeft + (r.startW - w); }
    if (r.edge.includes('s')) h = Math.max(MIN_H, r.startH + dy);
    if (r.edge.includes('n')) { h = Math.max(MIN_H, r.startH - dy); y = r.startTop + (r.startH - h); }

    sizeRef.current = { w, h };
    posRef.current = { x, y };
    applyPanelStyle();
  };

  const onResizeUp = () => {
    resizeState.current = null;
    window.removeEventListener('mousemove', onResizeMove);
    window.removeEventListener('mouseup', onResizeUp);
  };

  if (minimized) {
    return (
      <div className="compose-minimized" onClick={() => setMinimized(false)}>
        <span>✏ {subject || 'New Email'}</span>
        <button onClick={e => { e.stopPropagation(); handleClose(); }}>✕</button>
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      className={`compose-panel${isMaximized ? ' maximized' : ''}`}
      style={{ width: DEFAULT_W, height: DEFAULT_H, right: 16, bottom: 16 }}
    >
      {/* Resize handles */}
      {!isMaximized && <>
        <div className="rh n"  onMouseDown={e => onResizeMouseDown(e, 'n')} />
        <div className="rh s"  onMouseDown={e => onResizeMouseDown(e, 's')} />
        <div className="rh e"  onMouseDown={e => onResizeMouseDown(e, 'e')} />
        <div className="rh w"  onMouseDown={e => onResizeMouseDown(e, 'w')} />
        <div className="rh ne" onMouseDown={e => onResizeMouseDown(e, 'ne')} />
        <div className="rh nw" onMouseDown={e => onResizeMouseDown(e, 'nw')} />
        <div className="rh se" onMouseDown={e => onResizeMouseDown(e, 'se')} />
        <div className="rh sw" onMouseDown={e => onResizeMouseDown(e, 'sw')} />
      </>}

      {/* Header — drag handle */}
      <div className="cp-header" onMouseDown={onHeaderMouseDown}>
        <span className="cp-title">
          {composeData?.to ? '↩ Reply' : '✏ New Email'}
        </span>
        <div className="cp-header-right">
          <div className="cp-tabs">
            <button className={`cp-tab${tab === 'compose' ? ' active' : ''}`}
              onClick={() => setTab('compose')}>Compose</button>
            <button className={`cp-tab${tab === 'templates' ? ' active' : ''}`}
              onClick={() => setTab('templates')}>Templates</button>
          </div>
          <button className="cp-ctrl-btn" onClick={() => setMinimized(true)} title="Minimize">—</button>
          <button className="cp-ctrl-btn" onClick={toggleMaximize} title={isMaximized ? 'Restore' : 'Maximize'}>
            {isMaximized ? '⧉' : '⬜'}
          </button>
          <button className="cp-ctrl-btn" onClick={handleClose} title="Close">✕</button>
        </div>
      </div>

      {tab === 'templates' ? (
        <div className="cp-templates">
          {Object.entries(TEMPLATES).map(([key, t]) => (
            <button key={key} className="template-item" onClick={() => applyTemplate(key)}>
              <div className="template-name">{t.label}</div>
              <div className="template-preview">{t.body.slice(0, 70)}…</div>
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="cp-fields">
            <div className="cp-field">
              <label className="cp-field-label">To</label>
              <input className="cp-field-input" placeholder="recipient@ellertek.co.tz"
                value={to} onChange={e => setTo(e.target.value)} autoFocus />
              <button className="cc-toggle" onClick={() => setShowCcBcc(s => !s)}>
                {showCcBcc ? 'Hide' : 'Cc/Bcc'}
              </button>
            </div>
            {showCcBcc && <>
              <div className="cp-divider" />
              <div className="cp-field">
                <label className="cp-field-label">Cc</label>
                <input className="cp-field-input" placeholder="cc@ellertek.co.tz"
                  value={cc} onChange={e => setCc(e.target.value)} />
              </div>
              <div className="cp-divider" />
              <div className="cp-field">
                <label className="cp-field-label">Bcc</label>
                <input className="cp-field-input" placeholder="bcc@ellertek.co.tz"
                  value={bcc} onChange={e => setBcc(e.target.value)} />
              </div>
            </>}
            <div className="cp-divider" />
            <div className="cp-field">
              <label className="cp-field-label">Subject</label>
              <input className="cp-field-input" placeholder="Subject"
                value={subject} onChange={e => setSubject(e.target.value)} />
            </div>
          </div>

          <div className="cp-toolbar">
            <button className="tb-btn" onClick={() => applyFormat('bold')} title="Bold"><b>B</b></button>
            <button className="tb-btn" onClick={() => applyFormat('italic')} title="Italic"><i>I</i></button>
            <button className="tb-btn" onClick={() => applyFormat('underline')} title="Underline"><u>U</u></button>
            <div className="tb-sep" />
            <button className="tb-btn" onClick={() => applyFormat('bullet')} title="Bullet">☰</button>
            <button className="tb-btn" title="Link">🔗</button>
            <button className="tb-btn" title="Attach">📎</button>
            <button className="tb-btn" title="Emoji">🙂</button>
            <div className="tb-sep" />
            <label className="tb-toggle">
              <input type="checkbox" checked={requestReadReceipt}
                onChange={e => setRequestReadReceipt(e.target.checked)} />
              <span>Read receipt</span>
            </label>
          </div>

          <div className="cp-body">
            <textarea ref={bodyRef} className="cp-textarea"
              placeholder="Write your message…"
              value={body} onChange={e => setBody(e.target.value)} />
          </div>

          {scheduleMode && (
            <div className="sched-row">
              <span>🕐</span>
              <label>Send at:</label>
              <input type="datetime-local" className="sched-input"
                value={scheduledFor} onChange={e => setScheduledFor(e.target.value)}
                min={new Date().toISOString().slice(0, 16)} />
              <button className="sched-cancel" onClick={() => setScheduleMode(false)}>✕</button>
            </div>
          )}

          {sendError && <div className="send-error">⚠ {sendError}</div>}

          <div className="cp-footer">
            <button className="btn-send" onClick={handleSend} disabled={sending}>
              {sending
                ? <span className="send-spinner" />
                : (scheduleMode ? '🕐 Schedule' : '➤ Send')}
            </button>
            <button className={`btn-schedule${scheduleMode ? ' active' : ''}`}
              onClick={() => setScheduleMode(s => !s)} title="Schedule send">🕐</button>
            <button className="btn-attach" title="Attach">📎</button>
            <button className="btn-draft" onClick={handleDraft}>💾 Draft</button>
          </div>
        </>
      )}
    </div>
  );
}
